import { useEffect, useState } from "react";
import React from "react";
import { Languages } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";

/**
 * useAutoTranslate — traduit automatiquement des champs texte vers la langue du lecteur.
 *
 * @param {Object} fields  ex. { title, description } (texte original de l'auteur)
 * @param {string} sourceLang  langue d'origine du contenu (champ `lang` du document)
 * @returns { values, translated, showingOriginal, toggle }
 *   - values          : les champs à afficher (traduits, ou originaux si toggle)
 *   - translated      : true si une traduction est disponible et active
 *   - showingOriginal : true si le lecteur a demandé la version originale
 *   - toggle          : bascule original / traduction
 *
 * La traduction n'est déclenchée QUE si la langue du contenu diffère de celle du lecteur.
 * Le backend met les traductions en cache (endpoint /api/translate).
 */
export function useAutoTranslate(fields, sourceLang) {
  const { lang } = useApp();
  const [translated, setTranslated] = useState(null);
  const [showingOriginal, setShowingOriginal] = useState(false);

  const needs = !!sourceLang && sourceLang !== lang &&
    Object.values(fields).some((v) => v && String(v).trim());

  useEffect(() => {
    let active = true;
    if (!needs) { setTranslated(null); return; }
    (async () => {
      // 1 retry après 1s si l'appel échoue (ex. 503 rate-limit LLM transitoire)
      for (let attempt = 0; attempt < 2 && active; attempt++) {
        try {
          const r = await api.post("/translate-fields", { fields, target: lang, source: sourceLang });
          if (active) setTranslated(r.data.fields);
          return;
        } catch {
          if (attempt === 0) await new Promise((res) => setTimeout(res, 1000));
          else if (active) setTranslated(null);
        }
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fields), sourceLang, lang]);

  const active = needs && !!translated && !showingOriginal;
  return {
    values: active ? translated : fields,
    translated: needs && !!translated,
    showingOriginal,
    toggle: () => setShowingOriginal((o) => !o),
  };
}

/** Badge « Traduit automatiquement » + bouton pour voir l'original. */
export function TranslationBadge({ tr }) {
  const { t } = useApp();
  if (!tr.translated) return null;
  return (
    <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid="translation-badge">
      <span
        title={t("translation_hint")}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-full"
      >
        <Languages className="h-3 w-3" /> {t("auto_translated")}
      </span>
      <button onClick={tr.toggle} className="text-[11px] font-semibold text-majorelle" data-testid="toggle-original-btn">
        {tr.showingOriginal ? t("show_translation") : t("show_original")}
      </button>
    </div>
  );
}
